import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { head, prop } from 'ramda';

import { CONTAINER, USER_API_PATH } from '@/lib/constants';
import { TContainerApi, TVehicleApi, TSurrealApi, TUserApi, TCompanyApi, TBrandApi, TPlansApi, TCustomerApi, TReportsApi, TServiceApi } from '@/lib/types';

export default class Api {
  axios: AxiosInstance;
  currentUser: any;
  currentParent: any;

  constructor(axios: AxiosInstance, user?: any) {
    this.axios = axios;
    this.currentUser = user ?? null;
    this.currentParent = user?.company ?? user ?? null;

    if (this.currentParent?.id) {
      this.currentParent.id = this.currentUser?.companyId ?? this.currentUser.id;
    }
  }

  containerApi: TContainerApi = {
    upload: (data) => this.postRequest(`/containers/${CONTAINER}/upload`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  }

  vehicleApi: TVehicleApi = {
    create: (data) => this.postRequest(`/${USER_API_PATH}/${this.currentParent.id}/vehicles`, data),
    updateById: (id, data) => this.putRequest(`/${USER_API_PATH}/${this.currentParent.id}/vehicles/${id}`, data),
    findById: (id, filter) => {
      filter = typeof filter === 'object'
        ? {
          ...filter,
          limit: 1,
          where: { id: +id }
        }
        : filter;

      return this.request('get', `/${USER_API_PATH}/${this.currentParent.id}/vehicles`, filter)
        .then(prop('data'))
        .then(head)
    }
  }

  customerApi: TCustomerApi = {
    create: (data) => this.postRequest(`/${USER_API_PATH}/${this.currentParent.id}/customers`, data),
    updateById: (id, data) => this.putRequest(`/${USER_API_PATH}/${this.currentParent.id}/customers/${id}`, data),
    find: (filter) => this.request('get', `/${USER_API_PATH}/${this.currentParent.id}/customers`, filter),
    findById: (id, filter) => {
      filter = typeof filter === 'object'
        ? {
          ...filter,
          limit: 1,
          where: { id: +id }
        }
        : filter;

      return this.request('get', `/${USER_API_PATH}/${this.currentParent.id}/customers`, filter)
        .then(prop('data'))
        .then(head)
    }
  }

  // this is for the logged user, can be 'user' , 'company', 'customerApp'
  userApi: TUserApi = {
    login: (data) => this.postRequest(`/${USER_API_PATH}/login?include=user`, data, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    }),
    logout: () => this.postRequest(`/${USER_API_PATH}/logout`, null),
    signup: (data) => this.postRequest(`/${USER_API_PATH}`, data),
    // deprecated refreshToken
    refreshToken: () => this.request('get', `/${USER_API_PATH}/refreshToken`, null),
    customers: {
      find: (filter) => this.request('get', `/${USER_API_PATH}/${this.currentParent.id}/customers`, filter),
      getCustomers: (filter) => this.request('get', `/${USER_API_PATH}/getCustomers`, filter),
    },
    getCurrent: (query?: any) => this.request('get', `/${USER_API_PATH}/current`, query),
    statusList: (filter) => this.request('get', `/${USER_API_PATH}/${this.currentParent.id}/statusList`, filter),
    update: (data) => this.patchRequest(`/${USER_API_PATH}/${this.currentUser.id}`, data),
  };

  companyApi: TCompanyApi = {
    login: (data) => this.postRequest(`/companies/login?include=user`, data, {
      headers: { 'Content-Type': 'application/json' },
      // withCredentials: true,
    }),
    logout: () => this.postRequest(`/companies/logout`, null),
    signup: (data) => this.postRequest(`/companies`, data),
    // deprecated refreshToken
    refreshToken: () => this.request('get', `/companies/refreshToken`, null),
    customers: {
      find: (query) => this.request('get', `/companies/${this.currentParent.id}/customers`, query),
      getCustomers: (query) => this.request('get', `/companies/getCustomers`, query),
    },
    getCurrent: () => this.request('get', `/companies/${this.currentParent.id}`),
    statusList: (query) => this.request('get', `/companies/${this.currentParent.id}/statusList`, query),
    getWorkshops: (query) => this.request('get', `/companies/${this.currentParent.id}/workshops`, query),
    update: (id, data) => this.patchRequest(`/companies/${id}`, data),
    find: (query) => this.request('get', `/companies`, query),
    findOne: (id, query) => this.request('get', `/companies/${id}`, query),
    loginAsMaster: (id) => this.postRequest(`/companies/${id}/loginAsMaster`, null),
    count: (query) => this.request('get', `/companies/count`, query),
    wapiLogout: (companyId) => this.postRequest(`/companies/wapi/logout`, { companyId }),
    wapiInit: (companyId) => this.postRequest(`/companies/wapi/init`, { companyId }),
    wapiReset: (companyId) => this.postRequest(`/companies/wapi/reset`, { companyId }),
    wapiGetInfo: (companyId) => this.postRequest(`/companies/wapi/getInfo`, { companyId }),
    payments: {
      create: (id, payment) => this.postRequest(`/companies/${id}/payments`, payment)
    },
    crm: (request) => this.request('get', `/companies/crm`, request),
    config: {
      get: (companyId, filter) => this.request('get', `/companies/${companyId}/configItems`, filter),
      update: (companyId, id, data) => this.putRequest(`/companies/${companyId}/configItems/${id}`, data),
      create: (companyId, data) => this.postRequest(`/companies/${companyId}/configItems`, data),
    },
    importKardex: (data) => this.postRequest(`/companies/importKardex`, data),
    importPackages: (data) => this.postRequest(`/companies/importPackages`, data),
    getAffiliateConfig: () => this.request('get', `/companies/getAffiliateConfig`),
    updatePartsItk: (data) => this.postRequest(`/companies/updatePartsItk`, data),
  };

  servicesApi: TServiceApi = {
    create: (data) => this.postRequest(`/${USER_API_PATH}/${this.currentParent.id}/services`, data),
    count: (query) => this.request('get', `/${USER_API_PATH}/${this.currentParent.id}/services/count`, query),
    // list: (data) => {
    //   const { startDate = moment().subtract(3, 'months'), endDate = moment().endOf('day') } = data;
    //   return this.postRequest(`/${USER_API_PATH}/companyWideServices`, {
    //     ...data, startDate, endDate
    //   });
    // },
    // findById: (id, filter) => {
    //   filter = typeof filter === 'object'
    //     ? {
    //       ...filter,
    //       limit: 1,
    //       where: { id: +id }
    //     }
    //     : filter;
    //   return this.request('get', `/${USER_API_PATH}/${this.currentParent.id}/services`, filter)
    //     .then(prop('data'))
    //     .then(map(servicePriceMapper))
    //     .then(head)
    // },
    find: (query) => this.request('get', `/${USER_API_PATH}/${this.currentParent.id}/services`, query),
    getLastNoOrder: () => this.postRequest('/Services/getLastNoOrder'),
    updateStatus: (data, id) => this.postRequest(`/Services/updateStatus?id=${id}`, data)

  }

  brandsApi: TBrandApi = {
    find: (query) => this.request('get', `/Brands`, query),
    findOne: (query) => this.request('get', `/Brands/findOne`, query),
  }

  plansApi: TPlansApi = {
    getPlansConfig: () => this.postRequest(`/plans/getPlansConfig`),
    getPlans: () => this.postRequest(`/plans/getPlans`),
  }

  reportsApi: TReportsApi = {
    endOfDay: (query) => this.request('get', `/Reports/endOfDay`, query),
  }

  surrealApi: TSurrealApi = {
    get: (query: any) => this.request(`get`, `/surreal`, query)
      .then(prop('data'))
  }

  async request(
    method: 'get' | 'put' | 'patch',
    url: string,
    query: any = {},
    config?: AxiosRequestConfig | undefined
  ) {
    if (query) {
      const [key] = Object.keys(query);
      url += `?${key}=${encodeURIComponent(JSON.stringify(query[key]))}`;
    }

    console.log(method, url, query);
    return this.axios[method](url, null, config);
  }

  async patchRequest(url: string, data: any) {
    return this.axios.patch(url, data);
  }

  async putRequest(url: string, data: any) {
    return this.axios.put(url, data);
  }

  async postRequest(url: string, data?: any, config?: AxiosRequestConfig<any> | undefined) {
    return this.axios.post(url, data ? data : '{}', config);
  }
}
